import { useState } from "react";
import { Button, Input, Radio, Select, Space, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { CUSTOM_COLUMN_TYPES, nextColumnId } from "./reportingUtils";

const CHANGE_SHOW_OPTIONS = [
  { value: "pct", label: "%" },
  { value: "abs", label: "Amount" },
];

const TYPE_LABELS = {
  [CUSTOM_COLUMN_TYPES.value]: "Value",
  [CUSTOM_COLUMN_TYPES.change]: "Change",
  [CUSTOM_COLUMN_TYPES.cagr]: "CAGR",
};

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  border: "1px solid #f0f0f0",
  borderRadius: 4,
  marginBottom: 6,
};

/**
 * Edits settings.customColumns (see CUSTOM_COLUMN_TYPES in reportingUtils).
 * periodOptions are grouped Select options; periodCats the flat periods.
 */
export default function CustomColumnsEditor({ columns = [], onChange, sourceOptions, periodOptions, periodCats }) {
  const [quickSource, setQuickSource] = useState();
  const [quickPeriods, setQuickPeriods] = useState([]);

  const sourceLabel = (key) =>
    sourceOptions.flatMap((g) => g.options).find((o) => o.value === key)?.label ?? "";
  const periodLabel = (key) => periodCats.find((c) => c.key === key)?.label ?? key ?? "";
  const valueLabel = (c) => c.label || `${sourceLabel(c.source)} ${periodLabel(c.period)}`.trim() || "(empty)";

  const valueColumnOptions = columns
    .filter((c) => c.type === CUSTOM_COLUMN_TYPES.value)
    .map((c) => ({ value: c.id, label: valueLabel(c) }));

  const patch = (id, change) => onChange(columns.map((c) => (c.id === id ? { ...c, ...change } : c)));
  const remove = (id) => onChange(columns.filter((c) => c.id !== id));
  const move = (index, direction) => {
    const next = [...columns];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    onChange(next);
  };

  // Change and CAGR default to the last two value columns.
  const add = (type) => {
    const id = nextColumnId(columns);
    if (type === CUSTOM_COLUMN_TYPES.value) {
      const last = [...columns].reverse().find((c) => c.type === CUSTOM_COLUMN_TYPES.value);
      return onChange([...columns, { id, type, source: last?.source, period: undefined }]);
    }
    const values = valueColumnOptions.slice(-2);
    const from = values.length === 2 ? values[0].value : undefined;
    const to = values[values.length - 1]?.value;
    onChange([...columns, { id, type, from, to, ...(type === CUSTOM_COLUMN_TYPES.change ? { show: "pct" } : {}) }]);
  };

  // One value column per selected period, in period order.
  const addQuick = () => {
    const selected = new Set(quickPeriods);
    let id = nextColumnId(columns);
    const added = periodCats
      .filter((c) => selected.has(c.key))
      .map((c) => ({ id: id++, type: CUSTOM_COLUMN_TYPES.value, source: quickSource, period: c.key }));
    onChange([...columns, ...added]);
    setQuickPeriods([]);
  };

  const rangeFields = (c) => (
    <>
      <Select
        size="small"
        style={{ width: 170 }}
        placeholder="From"
        value={c.from}
        options={valueColumnOptions}
        onChange={(from) => patch(c.id, { from })}
      />
      <span>→</span>
      <Select
        size="small"
        style={{ width: 170 }}
        placeholder="To"
        value={c.to}
        options={valueColumnOptions}
        onChange={(to) => patch(c.id, { to })}
      />
    </>
  );

  return (
    <div>
      {columns.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 6 }}>
          No columns yet. Add value columns first, then changes or CAGR between them.
        </div>
      )}

      {columns.map((c, index) => (
        <div key={c.id} style={rowStyle}>
          <strong style={{ width: 54 }}>{TYPE_LABELS[c.type]}</strong>

          {c.type === CUSTOM_COLUMN_TYPES.value && (
            <>
              <Select
                size="small"
                showSearch
                optionFilterProp="label"
                style={{ width: 190 }}
                placeholder="Business case/scenario"
                value={c.source}
                options={sourceOptions}
                onChange={(source) => patch(c.id, { source })}
              />
              <Select
                size="small"
                showSearch
                optionFilterProp="label"
                style={{ width: 150 }}
                placeholder="Period"
                value={c.period}
                options={periodOptions}
                onChange={(period) => patch(c.id, { period })}
              />
            </>
          )}

          {c.type === CUSTOM_COLUMN_TYPES.change && (
            <>
              {rangeFields(c)}
              <Radio.Group
                size="small"
                optionType="button"
                options={CHANGE_SHOW_OPTIONS}
                value={c.show || "pct"}
                onChange={(e) => patch(c.id, { show: e.target.value })}
              />
            </>
          )}

          {c.type === CUSTOM_COLUMN_TYPES.cagr && rangeFields(c)}

          <Input
            size="small"
            allowClear
            style={{ width: 150 }}
            placeholder="Header (optional)"
            value={c.label}
            onChange={(e) => patch(c.id, { label: e.target.value || undefined })}
          />

          <Space size={0} style={{ marginLeft: "auto" }}>
            <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, -1)} />
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={index === columns.length - 1}
              onClick={() => move(index, 1)}
            />
            <Tooltip title="Remove column">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(c.id)} />
            </Tooltip>
          </Space>
        </div>
      ))}

      <Space size={8} wrap style={{ marginTop: 4 }}>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => add(CUSTOM_COLUMN_TYPES.value)}>
          Value
        </Button>
        <Tooltip title={valueColumnOptions.length < 2 ? "Add two value columns first" : undefined}>
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            disabled={valueColumnOptions.length < 2}
            onClick={() => add(CUSTOM_COLUMN_TYPES.change)}
          >
            Change
          </Button>
        </Tooltip>
        <Tooltip title={valueColumnOptions.length < 2 ? "Add two value columns first" : undefined}>
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            disabled={valueColumnOptions.length < 2}
            onClick={() => add(CUSTOM_COLUMN_TYPES.cagr)}
          >
            CAGR
          </Button>
        </Tooltip>
      </Space>

      <div style={{ fontWeight: 600, margin: "12px 0 6px", fontSize: 12 }}>Add several periods at once</div>
      <Space size={6} wrap>
        <Select
          size="small"
          showSearch
          optionFilterProp="label"
          style={{ width: 190 }}
          placeholder="Business case/scenario"
          value={quickSource}
          options={sourceOptions}
          onChange={setQuickSource}
        />
        <Select
          size="small"
          mode="multiple"
          maxTagCount="responsive"
          optionFilterProp="label"
          style={{ width: 260 }}
          placeholder="Periods"
          value={quickPeriods}
          options={periodOptions}
          onChange={setQuickPeriods}
        />
        <Button size="small" disabled={!quickSource || quickPeriods.length === 0} onClick={addQuick}>
          Add columns
        </Button>
      </Space>
    </div>
  );
}
